import LoadingSpinner from '@/components/LoadingSpinner';
import { accessibilityDataAtom } from '@/global-store';
import { useFetchPageDetailReport, useFetchUrlMap } from '@http/queries';
import { Box } from '@mui/material';
import { useAtom } from 'jotai';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import IssueExportForm from '../IssueExportForm';
import {
    buildBulletListAdf,
    buildCodeBlockAdf,
    buildJiraAdfTable,
    formattedText,
    headingAdf,
    JIRA_HTML_SIZE_LIMIT,
    subHeadingWithTextAdf,
} from './adfUtils';

export function SiteIssuesExport({
    todosToExport,
    jiraProject,
    jiraUsers,
    linkedJiraUserID,
    issueExportFormRef,
}) {
    const { t } = useTranslation();

    const { data: urlMap, isLoading: urlMapIsLoading, isError: urlMapIsError } = useFetchUrlMap();

    const [accessibilityData] = useAtom(accessibilityDataAtom);

    const ruleId = todosToExport[0].props.ruleNum;
    const pageIndex = +todosToExport[0].props.pageIndex;

    const conformanceTarget = accessibilityData?.conformanceTarget.split(' + ');
    let impactconformanceTarget = `diff${conformanceTarget[0]}`;
    if (conformanceTarget[1]) impactconformanceTarget += `_BP`;
    const impactScore = accessibilityData.auditScores?.ruleImpacts
        ? accessibilityData.auditScores.ruleImpacts?.[ruleId]?.[impactconformanceTarget]
        : undefined;

    const {
        data: pageReport,
        isLoading,
        isError: pageReportLoadError,
    } = useFetchPageDetailReport(pageIndex);

    const eaRule = accessibilityData.eaRules[ruleId];
    const wcagChapter = eaRule.categories[0];
    const wcagLvl = accessibilityData.wcagRules[wcagChapter].wcagLvl;
    const title = accessibilityData.eaRules[ruleId]?.content || '(no name)';

    const sourcesRows = useMemo(() => {
        if (!pageReport || pageReportLoadError) return [];

        const pageRules = pageReport?.rules[ruleId];

        if (!pageRules?.sources?.length > 0) return [];

        const maxSourceHTMLLimit = JIRA_HTML_SIZE_LIMIT / pageRules.sources.length;

        const _sourcesRows = [];

        for (const source of pageRules.sources) {
            if (!source) continue;

            const errorInfo = {};
            const { xPath, metaData } = source;
            if (xPath) errorInfo.xPath = formattedText(xPath, 'code');
            if (metaData?.outerHtml)
                if (metaData?.outerHtml.length < Math.min(maxSourceHTMLLimit, 1000))
                    errorInfo.html = buildCodeBlockAdf(metaData.outerHtml, 'xml');
                else errorInfo.html = formattedText(`<${t('htmlTooLong')}>`, 'em');
            _sourcesRows.push(Object.values(errorInfo));
        }

        return _sourcesRows;
    }, [pageReport, pageReportLoadError]);

    const writtenURL = useMemo(() => {
        if (isLoading) return '';
        else return pageReport?.page ?? pageReport?.context?.pageUrl;
    }, [pageReport, isLoading]);

    const calculatedAuditScore = useMemo(() => {
        if (!writtenURL) return 0;

        return accessibilityData?.calcOverallAuditScore(
            accessibilityData?.auditScores?.perPage[writtenURL],
            false
        );
    }, [accessibilityData, writtenURL]);

    const totalErrorsOnPage = writtenURL
        ? accessibilityData?.calcTotalwarningsForConformanceTarget(
              accessibilityData?.report?.summary?.summaryPerPage?.[writtenURL]
          )
        : 0;

    if (!jiraUsers || isLoading) return <LoadingSpinner />;

    const headers = ['xPath', 'HTML'];

    const jiraBodyDescripiton = [];

    // Issue Title
    jiraBodyDescripiton.push(headingAdf(pageReport?.title || t('noPageTitle'), 2));
    if (eaRule.content) jiraBodyDescripiton.push(headingAdf(eaRule.content, 2));

    jiraBodyDescripiton.push(
        subHeadingWithTextAdf(`${t('page')} ${t('url')}`, writtenURL, writtenURL)
    );
    jiraBodyDescripiton.push(subHeadingWithTextAdf(t('pageRating'), `${calculatedAuditScore} %`));
    jiraBodyDescripiton.push(
        subHeadingWithTextAdf(t('totalErrorsForPage'), totalErrorsOnPage.toString())
    );
    jiraBodyDescripiton.push(
        subHeadingWithTextAdf(
            t('occurrencesOfSelectedRuleOnPage'),
            pageReport?.rules[ruleId]?.sources?.length ?? 0
        )
    );

    // Issue Description
    jiraBodyDescripiton.push(formattedText(t('issueDetailSubHead1'), 'strong'));
    jiraBodyDescripiton.push(formattedText(eaRule.explanation));

    // Issue How to fix
    jiraBodyDescripiton.push(formattedText(t('issueDetailSubHead2'), 'strong'));
    jiraBodyDescripiton.push(formattedText(eaRule.tip));

    // Issue properties

    jiraBodyDescripiton.push(formattedText(t('issueDetailSubHead'), 'strong'));
    const categories = accessibilityData?.eaRules?.[ruleId].categories.map(cat => {
        const level = accessibilityData.wcagRules[cat].wcagLvl;
        const formatedLevel = level === 'Best Practice' ? level : 'Level ' + level;
        return cat === 'Best Practice'
            ? `${formatedLevel} - ${accessibilityData.wcagRules[cat].name}`
            : {
                  type: 'text',
                  text: `${formatedLevel} - ${cat} ${accessibilityData.wcagRules[cat].name}`,
                  marks: [
                      {
                          type: 'link',
                          attrs: {
                              href: accessibilityData.wcagRules[cat].urlWCAG,
                          },
                      },
                  ],
              };
    });

    jiraBodyDescripiton.push(buildBulletListAdf(categories));

    jiraBodyDescripiton.push(
        subHeadingWithTextAdf(t('complexity'), t('issueComplexity+' + eaRule.complexity))
    );
    jiraBodyDescripiton.push(
        subHeadingWithTextAdf(t('responsibility'), t('issueComplexity+' + eaRule.responsibility))
    );
    if (impactScore)
        jiraBodyDescripiton.push(
            subHeadingWithTextAdf(t('issuesImpactScore'), impactScore.toString())
        );

    // page sources for selected rule
    if (sourcesRows.length > 0) {
        const tableAdf = buildJiraAdfTable(headers, sourcesRows);
        if (JSON.stringify(tableAdf).length < 25000) {
            jiraBodyDescripiton.push(headingAdf(t('failedOccurrencesOnPage'), 3));
            jiraBodyDescripiton.push(tableAdf);
        }
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <IssueExportForm
                ref={issueExportFormRef}
                todosToExport={todosToExport}
                jiraProject={jiraProject}
                jiraBodyDescripiton={jiraBodyDescripiton}
                jiraUsers={jiraUsers}
                linkedJiraUserID={linkedJiraUserID}
            />
        </Box>
    );
}
